import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import Picker, { Theme, EmojiStyle } from 'emoji-picker-react';
import {
  FormatColorText as FormatColorTextIcon,
  InsertEmoticon as InsertEmoticonIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Send as SendIcon,
  InsertDriveFile as FileIcon,
} from '@material-ui/icons';
import colors from '../../styles/colors';
import TiptapEditor from '../../pages/chat/TiptapEditor';
import {
  EditWrapper,
  EditContainer,
  EditorBody,
  AttachmentSection,
  ExistingBadge,
  NewFileCard,
  BottomToolbar,
  FloatingEmojiPicker,
} from './styles';

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const MessageEditTextarea = ({
  message,
  mentionUsers = [],
  existingAttachments = [],
  onRemoveExistingAttachment,
  onCancel,
  onSave,
}) => {
  const intl = useIntl();
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiBtnRef = useRef(null);
  const containerRef = useRef(null);

  const [showFormatBar, setShowFormatBar] = useState(false);
  const [openEmoji, setOpenEmoji] = useState(false);
  const [pickerPlacement, setPickerPlacement] = useState('top'); // 'top' or 'bottom'
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    if (editorRef.current && message) {
      editorRef.current.setContent(message.Message || '');
      editorRef.current.focus?.();
    }
  }, [message]);

  useEffect(() => {
    // Scroll edit box into view smoothly so toolbar & buttons are never cut off
    if (containerRef.current) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 50);
    }
  }, []);

  const toggleEmojiFlyout = () => {
    if (!openEmoji && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const pickerHeight = 420;

      // If space above edit box is less than picker height, open below; otherwise open above
      if (rect.top < pickerHeight + 20) {
        setPickerPlacement('bottom');
      } else {
        setPickerPlacement('top');
      }
    }
    setOpenEmoji((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        openEmoji &&
        !e.target.closest('.emoji-flyout-container') &&
        !e.target.closest('.emoji-trigger-btn')
      ) {
        setOpenEmoji(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openEmoji]);

  const handleFilesChosen = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
    e.target.value = '';
  };

  const handleRemoveNewFile = (idx) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    const rawHtml = editorRef.current?.getHTML?.() || '';
    const plainText = (editorRef.current?.getText?.() || '').trim();

    const hasRichFormatting =
      /<(strong|b|em|i|u|s|strike|del|a|span|code|blockquote|ul|ol|li)/i.test(
        rawHtml
      );
    const isHtml = message?.IsHtmlMessage || hasRichFormatting;
    const content = isHtml ? rawHtml : plainText;

    onSave({
      content,
      isHtmlMessage: isHtml,
      newFiles: selectedFiles,
      remainingExistingAttachments: existingAttachments,
    });
  };

  return (
    <EditWrapper>
      <EditContainer ref={containerRef}>
        {/* TipTap Input Body */}
        <EditorBody>
          <TiptapEditor
            ref={editorRef}
            placeholder={intl.formatMessage({
              id: 'EditMessage',
              defaultMessage: 'Edit message...',
            })}
            mentionUsers={mentionUsers}
            showToolbar={showFormatBar}
            onCloseToolbar={() => setShowFormatBar(false)}
            onSubmit={handleSave}
          />
        </EditorBody>

        {/* Existing Attachments */}
        {existingAttachments?.length > 0 && (
          <AttachmentSection>
            <p>
              {intl.formatMessage({
                id: 'ExistingFiles',
                defaultMessage: 'Existing files:',
              })}
            </p>
            <div className="items-row">
              {existingAttachments.map((att) => (
                <ExistingBadge key={att.Guid}>
                  <FileIcon style={{ fontSize: 16, color: '#4b5563' }} />
                  <span title={att.Name}>{att.Name}</span>
                  <button
                    type="button"
                    onClick={() => onRemoveExistingAttachment(att.Guid)}
                    title="Remove"
                  >
                    ×
                  </button>
                </ExistingBadge>
              ))}
            </div>
          </AttachmentSection>
        )}

        {/* Newly Added Files */}
        {selectedFiles?.length > 0 && (
          <AttachmentSection>
            <p>
              {intl.formatMessage({
                id: 'SelectedFiles',
                defaultMessage: 'Selected files:',
              })}
            </p>
            <div className="items-row">
              {selectedFiles.map((file, idx) => (
                <NewFileCard key={idx}>
                  <button
                    type="button"
                    className="remove-btn"
                    onClick={() => handleRemoveNewFile(idx)}
                    title="Remove"
                  >
                    <CloseIcon />
                  </button>
                  <div className="preview-box">
                    {file.type?.startsWith('image/') ? (
                      <img src={URL.createObjectURL(file)} alt="preview" />
                    ) : (
                      <FileIcon />
                    )}
                  </div>
                  <div className="file-meta">
                    <span className="title" title={file.name}>
                      {file.name}
                    </span>
                    <span className="subtitle">
                      {file.type?.split('/')[1]?.toUpperCase() || 'FILE'} |{' '}
                      {formatBytes(file.size)}
                    </span>
                  </div>
                </NewFileCard>
              ))}
            </div>
          </AttachmentSection>
        )}

        {/* Bottom Toolbar — Exactly matching composer-bottom-row */}
        <BottomToolbar>
          <div
            className="composer-bottom-row"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Text Formatting */}
            <button
              type="button"
              className={`action-icon-btn ${
                showFormatBar ? 'active-format' : ''
              }`}
              title="Text Formatting"
              onClick={() => setShowFormatBar(!showFormatBar)}
            >
              <FormatColorTextIcon />
            </button>

            {/* Emojis */}
            <button
              ref={emojiBtnRef}
              type="button"
              className={`action-icon-btn emoji-trigger-btn ${
                openEmoji ? 'active-format' : ''
              }`}
              title={intl.formatMessage({
                id: 'Emojis',
                defaultMessage: 'Emojis',
              })}
              onClick={toggleEmojiFlyout}
            >
              <InsertEmoticonIcon />
            </button>

            {/* Attach Files (Same Paperclip icon) */}
            <button
              type="button"
              className="action-icon-btn"
              title={intl.formatMessage({
                id: 'AttachFiles',
                defaultMessage: 'Attach Files',
              })}
              onClick={() => fileInputRef.current?.click()}
            >
              <AttachFileIcon />
            </button>
            <input
              ref={fileInputRef}
              multiple
              type="file"
              style={{ display: 'none' }}
              onChange={handleFilesChosen}
            />

            {/* Divider */}
            <div className="action-divider" />

            {/* Cancel (Transparent action icon) */}
            <button
              type="button"
              className="action-icon-btn"
              title={intl.formatMessage({
                id: 'Cancel',
                defaultMessage: 'Cancel',
              })}
              onClick={onCancel}
            >
              <CloseIcon />
            </button>

            {/* Save / Send (Open arrow SendIcon) */}
            <button
              type="button"
              className="action-icon-btn"
              title={intl.formatMessage({
                id: 'Send',
                defaultMessage: 'Send',
              })}
              onClick={handleSave}
            >
              <SendIcon />
            </button>
          </div>
        </BottomToolbar>

        {/* Floating Emoji Picker Flyout */}
        {openEmoji && (
          <FloatingEmojiPicker
            className="emoji-flyout-container"
            $placement={pickerPlacement}
          >
            <Picker
              emojiStyle={EmojiStyle.NATIVE}
              lazyLoadEmojis
              onEmojiClick={(emojiData, event) => {
                editorRef.current?.insertEmoji(emojiData.emoji);
                setOpenEmoji(false);
              }}
            />
          </FloatingEmojiPicker>
        )}
      </EditContainer>
    </EditWrapper>
  );
};

export default MessageEditTextarea;
